import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Film, Camera, Wrench } from "lucide-react";
import CameraCancelHint from "@/components/CameraCancelHint";
import { uploadWatchMedia, patchWatchMedia, fileUrl } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const PHOTO_SLOTS = [
  { key: "photo_before_front", slot: "before_front", label: "Start · Front", group: "Before work" },
  { key: "photo_before_back",  slot: "before_back",  label: "Start · Back",  group: "Before work" },
  { key: "photo_after_front",  slot: "after_front",  label: "After · Front", group: "After work" },
  { key: "photo_after_back",   slot: "after_back",   label: "After · Back",  group: "After work" },
  { key: "photo_movement_disassembled", slot: "movement_disassembled", label: "Movement disassembled", group: "Teardown" },
];

function PhotoSlot({ watch, field, slot, label, onSaved }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const path = watch[field];

  const pickCamera = () => cameraRef.current?.click();
  const pickGallery = () => galleryRef.current?.click();

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setProgress(0);
    try {
      const { path: newPath } = await uploadWatchMedia(slot, f, setProgress);
      await patchWatchMedia(watch.id, { [field]: newPath });
      toast.success(`${label} saved`);
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setBusy(false); setProgress(0);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      await patchWatchMedia(watch.id, { [field]: null });
      toast.success(`${label} removed`);
      onSaved?.();
    } catch {
      toast.error("Could not remove");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col" data-testid={`photo-slot-${slot}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">{label}</div>
      <div
        onClick={!path && !busy ? pickCamera : undefined}
        className={`relative bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm overflow-hidden flex items-center justify-center
          ${path ? "min-h-[180px] max-h-[420px]" : "aspect-[4/3]"}
          ${!path && !busy ? "cursor-pointer hover:border-[#D4AF37]/60 transition-colors" : ""}`}
      >
        {path ? (
          <img src={fileUrl(path)} alt={label} className="max-h-[420px] w-auto max-w-full object-contain" />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center text-[#5A4620] gap-1.5">
            <Camera size={22} />
            <span className="text-[11px] uppercase tracking-[0.2em]">Tap to capture</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-[#0A0604]/40 flex items-center justify-center">
            <Loader2 size={20} className="text-[#D4AF37] animate-spin" />
            {progress > 0 && <span className="ml-2 text-xs text-[#F9F0D4] font-mono-data">{progress}%</span>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <button onClick={pickCamera} disabled={busy} className="text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] disabled:opacity-40 inline-flex items-center gap-1" data-testid={`photo-camera-${slot}`}>
          <Camera size={11} /> Camera
        </button>
        <button onClick={pickGallery} disabled={busy} className="text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] disabled:opacity-40 inline-flex items-center gap-1" data-testid={`photo-gallery-${slot}`}>
          <Upload size={11} /> Gallery
        </button>
        {path && (
          <button onClick={onRemove} disabled={busy} className="text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-rose-500 disabled:opacity-40 inline-flex items-center gap-1 ml-auto" data-testid={`photo-remove-${slot}`}>
            <Trash2 size={11} /> Remove
          </button>
        )}
      </div>
      {/* `capture` on iOS opens the camera directly; desktop falls back to file chooser. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onChange} />
      <input ref={galleryRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={onChange} />
    </div>
  );
}

function VideoSlot({ watch, onSaved }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const path = watch.video_movement;

  const pick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    // Picker accepts photos too (so iOS Photos shows them alongside videos
    // for context), but only video files are valid for this slot.
    const isVideo =
      (f.type && f.type.startsWith("video/")) ||
      /\.(mp4|mov|m4v|webm|qt)$/i.test(f.name);
    if (!isVideo) {
      toast.error("That's not a video file — pick the movement clip instead.");
      return;
    }
    setBusy(true); setProgress(0);
    try {
      const { path: newPath } = await uploadWatchMedia("movement", f, setProgress);
      await patchWatchMedia(watch.id, { video_movement: newPath });
      toast.success("Movement video saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false); setProgress(0);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      await patchWatchMedia(watch.id, { video_movement: null });
      toast.success("Video removed");
      onSaved?.();
    } catch {
      toast.error("Could not remove");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#8B6A1F]/45 bg-[#F9F0D4] rounded-sm p-5" data-testid="video-slot-movement">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-serif-display text-2xl text-[#0A0604] leading-none">Movement Running</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mt-2">~20 second clip · mp4 / mov / webm · up to 200 MB</div>
        </div>
        <Film size={18} className="text-[#5A4620]" />
      </div>

      {path ? (
        <video
          key={path}
          controls
          preload="metadata"
          className="w-full max-w-xl aspect-video bg-[#1A1208] rounded-sm"
          data-testid="video-movement-player"
        >
          <source src={fileUrl(path)} />
        </video>
      ) : (
        <div
          onClick={!busy ? pick : undefined}
          className="w-full max-w-xl aspect-video border border-dashed border-[#8B6A1F]/60 rounded-sm cursor-pointer hover:border-[#D4AF37]/70 transition-colors flex flex-col items-center justify-center text-[#5A4620] gap-2"
          data-testid="video-empty-dropzone"
        >
          <Film size={28} />
          <span className="text-xs uppercase tracking-[0.2em]">Upload movement clip</span>
        </div>
      )}

      {busy && (
        <div className="mt-3 flex items-center gap-2 text-xs text-[#3A2A10]">
          <Loader2 size={14} className="animate-spin" />
          Uploading… <span className="font-mono-data">{progress}%</span>
        </div>
      )}

      <div className="flex items-center gap-3 mt-3">
        <button onClick={pick} disabled={busy} className="text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] disabled:opacity-40 inline-flex items-center gap-1" data-testid="video-upload-movement">
          <Upload size={12} /> {path ? "Replace video" : "Upload video"}
        </button>
        {path && (
          <button onClick={onRemove} disabled={busy} className="text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-rose-500 disabled:opacity-40 inline-flex items-center gap-1" data-testid="video-remove-movement">
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="video/*,image/*,.mp4,.mov,.m4v,.webm" className="hidden" onChange={onChange} />
    </div>
  );
}

export default function WatchMediaPanel({ watch, onSaved }) {
  if (!watch) return null;
  const beforeSlots = PHOTO_SLOTS.filter((s) => s.group === "Before work");
  const afterSlots = PHOTO_SLOTS.filter((s) => s.group === "After work");
  const teardownSlots = PHOTO_SLOTS.filter((s) => s.group === "Teardown");

  const hasFrontPair = watch.photo_before_front && watch.photo_after_front;
  const hasBackPair  = watch.photo_before_back  && watch.photo_after_back;
  const hasAnyPair = hasFrontPair || hasBackPair;

  return (
    <div className="space-y-8" data-testid="watch-media-panel">
      <CameraCancelHint testid="media-camera-cancel-hint" />
      {hasAnyPair && (
        <section data-testid="before-after-comparison">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-3">Before / After comparison</div>
          <div className="text-xs text-[#3A2A10] mb-4">Drag the gold handle to reveal the restoration.</div>
          <div className={`grid gap-6 ${hasFrontPair && hasBackPair ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
            {hasFrontPair && (
              <BeforeAfterSlider
                beforePath={watch.photo_before_front}
                afterPath={watch.photo_after_front}
                label="Front"
              />
            )}
            {hasBackPair && (
              <BeforeAfterSlider
                beforePath={watch.photo_before_back}
                afterPath={watch.photo_after_back}
                label="Back"
              />
            )}
          </div>
        </section>
      )}

      <section>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-3">Before work</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {beforeSlots.map((s) => (
            <PhotoSlot key={s.key} watch={watch} field={s.key} slot={s.slot} label={s.label} onSaved={onSaved} />
          ))}
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-3 flex items-center gap-2">
          <Wrench size={12} /> Teardown
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {teardownSlots.map((s) => (
            <PhotoSlot key={s.key} watch={watch} field={s.key} slot={s.slot} label={s.label} onSaved={onSaved} />
          ))}
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-3">After work</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {afterSlots.map((s) => (
            <PhotoSlot key={s.key} watch={watch} field={s.key} slot={s.slot} label={s.label} onSaved={onSaved} />
          ))}
        </div>
      </section>

      <section>
        <VideoSlot watch={watch} onSaved={onSaved} />
      </section>
    </div>
  );
}
